import {
  React,
  useEffect,
  useMemo,
} from 'react';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Descriptions } from 'antd';
import {
  categoryMap,
  defaultCostcodeHeaders,
  defaultEstimateHeaders,
  parseDate,
} from './ProjectBudgetTrackingImportHelpers';
import ProjectBudgetTrackingUnphased from '../ProjectBudgetTrackingUnphased';
import ProjectBudgetTrackingPhases from '../ProjectBudgetTrackingPhases';

const costcodeCategoryKeys = new Set(['hours', 'days', 'estimatedCost', 'estimatedQuantity', 'units']);

function ProjectBudgetTrackingImportPreview({
  divisionId,
  sheetJSONs,
  parsedData,
  setParsedData,
  estimateSheet,
  costcodeSheet,
  headers,
}) {
  const categories = useSelector((state) => state.costcodes.categories);

  const { phaseCodes = [], unphasedCostcodes = [], estimates = {} } = parsedData || {};

  useEffect(() => {
    // Parse out estimate and costcode data from sheet
    if (sheetJSONs && headers) {
      const estimateRows = sheetJSONs[estimateSheet] ?? [];
      const costcodeRows = sheetJSONs[costcodeSheet] ?? [];

      let hasDates = false;
      // Get estimate data
      const estimateData = {};
      if (estimateRows.length) {
        // Retrieve data from mapped headers
        defaultEstimateHeaders.forEach(({ key: estimateKey }) => {
          const headerKey = headers[estimateKey];
          estimateData[estimateKey] = estimateRows[0][headerKey] || null;
        });
        const { startDate, endDate } = estimateData;
        if (startDate || endDate) {
          hasDates = true;
        }
        estimateData.startDate = parseDate(startDate);
        estimateData.endDate = parseDate(endDate);
      }

      // Get Costcode Data
      // Deduplicate costcodes by code and by (name + categoryId) across all phases
      // so duplicates only produce one DB row, with all phases linking to the same id.
      // This matches the two unique keys on the costcodes table:
      //   UNIQUE KEY code  (code, companyId, type)
      //   UNIQUE KEY name  (name, companyId, type, categoryId)
      const phaseMap = {};
      const unphased = [];
      const idxBase = DateTime.local().toMillis();
      const costcodeByCode = {};
      const costcodeByNameCategory = {};
      let idxCounter = 0;
      costcodeRows.forEach((row) => {
        const costcode = {
          active: 1,
          divisionId,
          isNew: true,
          categoryAssignments: {},
        };

        const categoryAssignment = {};

        // Retrieve data from mapped headers
        defaultCostcodeHeaders.forEach(({ key: costcodeKey }) => {
          const headerKey = headers[costcodeKey];
          if (costcodeCategoryKeys.has(costcodeKey)) {
            categoryAssignment[costcodeKey] = row[headerKey] || null;
          } else {
            costcode[costcodeKey] = row[headerKey] || null;
          }
        });

        const {
          name,
          code,
          startDate,
          endDate,
          phase,
          description,
          category,
        } = costcode;

        // Don't include if invalid
        if (!name || !code || !category || !description) {
          return;
        }

        // parse to string if number
        costcode.name = costcode.name.toString();
        costcode.description = costcode.description.toString();
        costcode.code = costcode.code.toString();

        // Parse category
        const parsedCategoryName = categoryMap[category.toLowerCase()];
        const { id: categoryId } = categories[parsedCategoryName] || {};
        if (!categoryId) {
          return;
        }

        categoryAssignment.defaultCategoryId = categoryId;

        delete costcode.category;
        costcode.categoryIds = [categoryId];

        // Cleanup invalid fields depending on category
        if (parsedCategoryName === 'Labor') {
          delete categoryAssignment.estimatedQuantity;
          delete categoryAssignment.units;
        }
        if (parsedCategoryName === 'Material') {
          delete categoryAssignment.hours;
        }
        if (parsedCategoryName === 'Overhead') {
          delete categoryAssignment.estimatedQuantity;
          delete categoryAssignment.units;
        }
        if (parsedCategoryName === 'Equipment') {
          delete categoryAssignment.estimatedQuantity;
          delete categoryAssignment.units;
        }

        // Reuse the existing costcode object if we've already seen this code
        // or this (name + categoryId) combo, so duplicates across phases share
        // the same id and only produce one costcodes row in the DB.
        const nameKey = `${costcode.name}-${categoryId}`;
        const existingCostcode = costcodeByCode[costcode.code] ?? costcodeByNameCategory[nameKey];
        const resolvedCostcode = existingCostcode ?? {
          ...costcode,
          id: idxBase + idxCounter++,
        };

        if (!existingCostcode) {
          resolvedCostcode.categoryAssignments[categoryId] = categoryAssignment;

          // Parse dates
          if (startDate || endDate) {
            hasDates = true;
          }
          resolvedCostcode.startDate = parseDate(startDate);
          resolvedCostcode.endDate = parseDate(endDate);

          costcodeByCode[resolvedCostcode.code] = resolvedCostcode;
          costcodeByNameCategory[nameKey] = resolvedCostcode;
        }

        // Parse phases
        if (phase) {
          resolvedCostcode.phased = true;

          if (!phaseMap[phase]) {
            phaseMap[phase] = {
              costcodes: [],
              name: phase.toString(),
              description: ' ',
            };
          }
          // Only add to this phase if not already linked (same code, same phase)
          const alreadyLinked = phaseMap[phase].costcodes.some((c) => c.id === resolvedCostcode.id);
          if (!alreadyLinked) {
            const phaseCostcode = existingCostcode
              ? { ...resolvedCostcode, categoryAssignments: { [categoryId]: categoryAssignment } }
              : resolvedCostcode;
            phaseMap[phase].costcodes.push(phaseCostcode);
          }
        } else if (!existingCostcode) {
          unphased.push(resolvedCostcode);
        }
      });

      setParsedData({
        phaseCodes: Object.values(phaseMap),
        unphasedCostcodes: unphased,
        estimates: estimateData,
        hasDates,
      });
    } else {
      setParsedData({});
    }
  }, [divisionId, sheetJSONs, estimateSheet, costcodeSheet, headers]);

  const estimatesSection = useMemo(() => (
    <Descriptions title="Estimates">
      {defaultEstimateHeaders.map((defaultHeader) => (
        <Descriptions.Item label={defaultHeader.title} key={defaultHeader.key}>
          {
            (['startDate', 'endDate'].includes(defaultHeader.key) && estimates[defaultHeader.key])
              ? DateTime.fromMillis(estimates[defaultHeader.key]).toLocaleString(DateTime.DATE_FULL)
              : estimates[defaultHeader.key]
          }
        </Descriptions.Item>
      ))}
    </Descriptions>
  ), [estimates]);

  return (
    <>
      {estimatesSection}
      <ProjectBudgetTrackingUnphased
        categories={categories}
        unphasedCostcodes={unphasedCostcodes}
      />
      <ProjectBudgetTrackingPhases
        dataSource={phaseCodes}
        categories={categories}
      />
    </>
  );
}

ProjectBudgetTrackingImportPreview.propTypes = {
  divisionId: PropTypes.string.isRequired,
  parsedData: PropTypes.object.isRequired,
  setParsedData: PropTypes.func.isRequired,
  sheetJSONs: PropTypes.object,
  estimateSheet: PropTypes.string,
  costcodeSheet: PropTypes.string,
  headers: PropTypes.object,
};

ProjectBudgetTrackingImportPreview.defaultProps = {
  sheetJSONs: null,
  estimateSheet: null,
  costcodeSheet: null,
  headers: null,
};

export default ProjectBudgetTrackingImportPreview;
